import type { AvailabilityWindow, ProfileListening } from '../types/availability';

/**
 * Listening-hours FORMATTING for the "Your listening" card and the schedule
 * editor. What is available, today and next is the server's
 * (GET me/availability); nothing here decides it.
 */

export const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
export const DAY_LONG = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;
/** The editor lists the week Monday first, as the artboard does. */
export const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0] as const;

const MINUTES_PER_DAY = 1440;

/** The schedule editor's route (B6b: the 1:1 page's "Your listening" card links here). */
export const LISTENING_SCHEDULE_PATH = '/app/empathy/listening-schedule';

/** "Pause for today"'s helper (B6a), the same words in both of its places. */
export const PAUSE_HELPER =
  'Pauses your hours for the rest of today, in all your groups and 1-on-1. Booked calls still happen.';

/** The Empathy page's 1-on-1 tab: where "request empathy" goes with no group in common (R1). */
export const ONE_ON_ONE_PATH = '/app/empathy';
export const ONE_ON_ONE_STATE = { tab: '1on1' } as const;

/** The SPA path of a group's Empathy tab. */
export function cohortEmpathyTabPath(cohortId: number): string {
  return `/app/circles/cohorts/${cohortId}?tab=empathy`;
}

/** "19:00" -> 1140; "24:00" -> 1440; null when not a clock. */
export function parseClock(value: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(value);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (min > 59 || h > 24 || (h === 24 && min !== 0)) return null;
  return h * 60 + min;
}

/** A window as [start, end) minutes from its start day's midnight; end > start (past 1440 = next day). */
export function windowMinutes(
  w: Pick<AvailabilityWindow, 'start' | 'end'>,
): [number, number] | null {
  const start = parseClock(w.start);
  let end = parseClock(w.end);
  if (start === null || end === null || start === MINUTES_PER_DAY) return null;
  if (end <= start) end += MINUTES_PER_DAY;
  return [start, end];
}

function clock12(minute: number): { text: string; meridiem: 'am' | 'pm' } {
  const m = ((minute % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  const h24 = Math.floor(m / 60);
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  const text = m % 60 === 0 ? String(h12) : `${h12}:${String(m % 60).padStart(2, '0')}`;
  return { text, meridiem: h24 < 12 ? 'am' : 'pm' };
}

/**
 * "7–9 pm", "11:30 am–12:30 pm", "10 pm–12 am", "All day" — the server's
 * format (GiverAvailabilityService::range), so the card, the editor and the
 * summaries the server writes read alike.
 */
export function formatRange(startMinute: number, endMinute: number): string {
  const a = clock12(startMinute);
  const b = clock12(endMinute);
  if (endMinute - startMinute >= MINUTES_PER_DAY) {
    return startMinute % MINUTES_PER_DAY === 0 ? 'All day' : `24 h from ${a.text} ${a.meridiem}`;
  }
  if (a.meridiem === b.meridiem && endMinute - startMinute < 720) {
    return `${a.text}–${b.text} ${b.meridiem}`;
  }
  return `${a.text} ${a.meridiem}–${b.text} ${b.meridiem}`;
}

export function formatWindow(w: Pick<AvailabilityWindow, 'start' | 'end'>): string {
  const mins = windowMinutes(w);
  return mins ? formatRange(mins[0], mins[1]) : `${w.start} – ${w.end}`;
}

/**
 * The profile's `listening` key, or null. The server sends it to any
 * signed-in viewer the giver may be booked by (R1: no shared group needed;
 * `shared_groups` lists the ones they do share, often none), so its absence
 * is the signal: nothing is inferred from anything else on the payload.
 */
export function readProfileListening(profile: object): ProfileListening | null {
  if (!Object.prototype.hasOwnProperty.call(profile, 'listening')) return null;
  const value = (profile as { listening?: unknown }).listening as
    | Partial<ProfileListening>
    | null
    | undefined;
  if (!value || typeof value.summary !== 'string' || !Array.isArray(value.shared_groups))
    return null;
  const groups = value.shared_groups.filter(
    (g): g is { id: number; name: string } =>
      !!g && typeof g === 'object' && typeof g.id === 'number' && typeof g.name === 'string',
  );
  return {
    summary: value.summary,
    ...(typeof value.timezone === 'string' ? { timezone: value.timezone } : {}),
    shared_groups: groups,
    ...(typeof value.accepts_bookings === 'boolean'
      ? { accepts_bookings: value.accepts_bookings }
      : {}),
  };
}

/** "A", "A and B", "A, B and C". */
export function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** The device's IANA zone, or null where Intl cannot say. */
export function deviceTimeZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

/**
 * One "{days} · {range}" part of the server's hours summary
 * (GiverAvailabilityService::summarize joins them with "; "). `days` is null
 * for a bare range, which is what a part without " · " is.
 */
export interface HoursSegment {
  days: string | null;
  range: string;
}

/** The server's summary, split for layout only: nothing is re-derived (E2). */
export function hoursSegments(summary: string | null | undefined): HoursSegment[] {
  if (!summary) return [];
  return summary
    .split('; ')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const at = part.indexOf(' · ');
      return at === -1
        ? { days: null, range: part }
        : { days: part.slice(0, at), range: part.slice(at + 3) };
    });
}

/**
 * The summarising rule (E2): up to two segments show as they are; a longer
 * schedule shows its first segment and "+N more" ("Weekdays · 7–9 pm · +2
 * more"), never "+1 more" in place of the one line it would save.
 */
export function summariseHours(segments: HoursSegment[]): {
  shown: HoursSegment[];
  more: number;
} {
  if (segments.length <= 2) return { shown: segments, more: 0 };
  return { shown: segments.slice(0, 1), more: segments.length - 1 };
}
